import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Play, 
  Lock, 
  Unlock, 
  X, 
  Sparkles, 
  AlertCircle,
  CreditCard
} from 'lucide-react';

export default function Courses() {
  const navigate = useNavigate();
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const [userPurchases, setUserPurchases] = useState([]);
  const [loginAlert, setLoginAlert] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Check logged in user from localStorage
  const loggedInUser = JSON.parse(localStorage.getItem('user'));

  // Course Data Structure (3 Specified Courses for 2026)
  const coursesList = [
    {
      id: "mf-beginners-2026",
      title: "Mutual Fund Beginners Guide 2026",
      subtitle: "Complete foundational roadmap to equity & debt SIPs, compounding math, and zero-error portfolio setup.",
      price: 1499,
      originalPrice: 3999,
      badge: "Beginner Friendly",
      demoVideoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      videos: [
        { id: 1, title: "01. Introduction to Mutual Funds in 2026", duration: "12:45 mins", isFree: true, url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
        { id: 2, title: "02. Direct vs Regular Plans & Expense Ratios Explained", duration: "18:20 mins", isFree: false, url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
        { id: 3, title: "03. How to Pick Top Performing Equity SIPs", duration: "25:10 mins", isFree: false, url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
        { id: 4, title: "04. Debt Funds & Emergency Capital Protection", duration: "15:30 mins", isFree: false, url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
        { id: 5, title: "05. Step-by-Step Portfolio Rebalancing Rules", duration: "22:00 mins", isFree: false, url: "https://www.youtube.com/embed/dQw4w9WgXcQ" }
      ]
    },
    {
      id: "mf-advance-2026",
      title: "Mutual Fund Advance 2026",
      subtitle: "Advanced asset allocation, factor investing, tactical rebalancing, and tax-efficient withdrawal strategies (SWP).",
      price: 2999,
      originalPrice: 6999,
      badge: "Advanced Strategy",
      demoVideoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      videos: [
        { id: 1, title: "01. Module Overview & Macro Market Structure", duration: "14:10 mins", isFree: true, url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
        { id: 2, title: "02. Factor & Smart Beta Investing Framework", duration: "30:45 mins", isFree: false, url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
        { id: 3, title: "03. Tactical Asset Allocation during Market Corrections", duration: "28:15 mins", isFree: false, url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
        { id: 4, title: "04. SWP Tax Strategy & Retirement Income Engine", duration: "35:00 mins", isFree: false, url: "https://www.youtube.com/embed/dQw4w9WgXcQ" }
      ]
    },
    {
      id: "trading-start-2026",
      title: "How to Start Trading in 2026",
      subtitle: "Price action, risk management, liquidity zones, and live chart execution rules for working professionals.",
      price: 3499,
      originalPrice: 8999,
      badge: "High Demand",
      demoVideoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      videos: [
        { id: 1, title: "01. Market Structure & Reading Price Action First", duration: "16:00 mins", isFree: true, url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
        { id: 2, title: "02. The 1% Risk Rule & Position Sizing Mastery", duration: "24:30 mins", isFree: false, url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
        { id: 3, title: "03. Identifying High-Probability Trade Setups", duration: "32:10 mins", isFree: false, url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
        { id: 4, title: "04. Controlling Trading Psychology & FOMO", duration: "20:15 mins", isFree: false, url: "https://www.youtube.com/embed/dQw4w9WgXcQ" },
        { id: 5, title: "05. Live Trade Journaling & Weekly Execution Breakdown", duration: "40:00 mins", isFree: false, url: "https://www.youtube.com/embed/dQw4w9WgXcQ" }
      ]
    }
  ];

  // Fetch Purchased Courses when page loads using Relative Path
  useEffect(() => {
    if (loggedInUser && loggedInUser.id) {
      fetch(`/api/user-purchases/${loggedInUser.id}`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setUserPurchases(data.purchasedCourseIds);
          }
        })
        .catch(err => console.error("Error fetching purchases:", err));
    }
  }, [loggedInUser]);

  // Check if current user has purchased a specific course
  const isPurchased = (courseId) => {
    return userPurchases.includes(courseId);
  };

  // Handle Purchase Button Click using Relative Path
  const handlePurchaseClick = (course) => {
    if (!loggedInUser) {
      setLoginAlert(true);
      return;
    }

    // Process Mock Payment
    setProcessingPayment(true);
    fetch('/api/purchase', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: loggedInUser.id,
        userEmail: loggedInUser.email,
        courseId: course.id,
        amount: course.price
      })
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setUserPurchases([...userPurchases, course.id]);
          setPaymentSuccess(true);
        } else {
          alert(data.message || "Payment failed.");
        }
      })
      .catch(err => alert("Server error processing payment."))
      .finally(() => setProcessingPayment(false));
  };

  return (
    <div className="bg-[#FAF8F5] text-[#0B2540] min-h-screen font-sans pb-20 selection:bg-[#16325B] selection:text-white">
      
      {/* 1. Page Header */}
      <section className="bg-[#0B2239] text-white pt-16 pb-20 px-6 text-center relative overflow-hidden">
        <div className="max-w-4xl mx-auto space-y-4 relative z-10">
          <div className="inline-flex items-center gap-2 bg-[#16325B]/40 border border-[#3B82F6]/30 text-[#60A5FA] text-xs font-semibold px-4 py-1.5 rounded-full">
            <Sparkles className="w-4 h-4 text-[#60A5FA]" />
            2026 Flagship Curriculum
          </div>
          <h1 className="font-serif-title text-4xl sm:text-6xl font-extrabold tracking-tight">
            Learn with <span className="text-[#60A5FA] italic font-normal">Haritika</span>
          </h1>
          <p className="text-gray-300 text-xs sm:text-sm max-w-xl mx-auto">
            Practical, rules-based financial education. Free demo videos included for every course.
          </p>
        </div>
      </section>

      {/* 2. Three Courses Cards Grid */}
      <section className="py-16 px-6 max-w-7xl mx-auto -mt-10 relative z-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {coursesList.map((course) => {
            const hasPurchased = isPurchased(course.id);

            return (
              <div 
                key={course.id}
                className="bg-white rounded-3xl overflow-hidden border border-gray-200/80 shadow-md hover:shadow-xl hover:border-[#16325B]/40 transition-all duration-300 flex flex-col justify-between"
              >
                <div>
                  {/* Video Thumbnail & Demo Trigger */}
                  <div className="h-52 w-full bg-slate-900 relative flex items-center justify-center overflow-hidden group">
                    <img 
                      src={`https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&q=80&w=800`} 
                      alt={course.title}
                      className="w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-500"
                    />
                    <button
                      onClick={() => {
                        setSelectedCourse(course);
                        setActiveVideo(course.videos[0]);
                      }}
                      className="absolute bg-[#16325B] hover:bg-[#0F213D] text-white p-4 rounded-full shadow-lg group-hover:scale-110 transition flex items-center gap-2 text-xs font-bold border border-blue-300/30"
                    >
                      <Play className="w-5 h-5 fill-current" />
                      <span>Watch Free Demo</span>
                    </button>

                    <div className="absolute top-3 left-3 bg-[#0B2540]/90 text-[#60A5FA] font-bold text-[10px] uppercase px-3 py-1 rounded-full border border-white/20">
                      {course.badge}
                    </div>
                  </div>

                  {/* Course Details */}
                  <div className="p-6 space-y-3">
                    <h3 className="font-serif-title text-xl font-bold text-[#0B2540] leading-snug">
                      {course.title}
                    </h3>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      {course.subtitle}
                    </p>

                    <div className="flex items-center gap-3 pt-2">
                      <span className="text-2xl font-extrabold text-[#0B2540]">₹{course.price}</span>
                      <span className="text-xs text-gray-400 line-through">₹{course.originalPrice}</span>
                      <span className="text-[10px] font-bold text-[#16325B] bg-[#EEF4FA] px-2 py-0.5 rounded border border-[#C7D7E8]">60% OFF</span>
                    </div>
                  </div>
                </div>

                {/* Bottom Actions */}
                <div className="p-6 pt-0 space-y-2">
                  {hasPurchased ? (
                    <button
                      onClick={() => {
                        setSelectedCourse(course);
                        setActiveVideo(course.videos[0]);
                      }}
                      className="w-full bg-[#EEF4FA] text-[#16325B] font-bold py-3.5 rounded-2xl text-xs flex items-center justify-center gap-2 border border-[#C7D7E8]"
                    >
                      <Unlock className="w-4 h-4" />
                      <span>Unlocked • View Course Videos</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => handlePurchaseClick(course)}
                      className="w-full bg-[#16325B] hover:bg-[#0F213D] text-white font-bold py-3.5 rounded-2xl text-xs transition duration-300 shadow-md flex items-center justify-center gap-2"
                    >
                      <CreditCard className="w-4 h-4" />
                      <span>Purchase Course (₹{course.price})</span>
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setSelectedCourse(course);
                      setActiveVideo(course.videos[0]);
                    }}
                    className="w-full text-center text-xs font-semibold text-gray-500 hover:text-[#0B2540] py-1 transition"
                  >
                    View Curriculum ({course.videos.length} Lessons)
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ========================================================= */}
      {/* 3. LOGIN NOTIFICATION MODAL (If Not Logged In)            */}
      {/* ========================================================= */}
      {loginAlert && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 text-center space-y-5 shadow-2xl relative border border-gray-100">
            <div className="w-12 h-12 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto">
              <AlertCircle className="w-7 h-7" />
            </div>
            
            <div className="space-y-2">
              <h3 className="font-serif-title text-xl font-bold text-[#0B2540]">Login Required</h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                Please login or create an account first to purchase and unlock course videos.
              </p>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={() => setLoginAlert(false)}
                className="w-1/2 bg-gray-100 text-gray-700 font-bold py-2.5 rounded-xl text-xs hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <Link
                to="/login"
                className="w-1/2 bg-[#16325B] text-white font-bold py-2.5 rounded-xl text-xs text-center hover:bg-[#0F213D] transition shadow-sm"
              >
                Go to Login
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* 4. COURSE PLAYER & VIDEO LIST MODAL                       */}
      {/* ========================================================= */}
      {selectedCourse && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl relative flex flex-col lg:flex-row border border-gray-200">
            
            {/* Close Button */}
            <button
              onClick={() => {
                setSelectedCourse(null);
                setPaymentSuccess(false);
              }}
              className="absolute top-4 right-4 z-20 bg-black/60 hover:bg-black text-white p-2 rounded-full transition"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Left: Video Player */}
            <div className="lg:w-2/3 bg-black flex flex-col justify-between p-4 relative">
              <div className="aspect-video w-full rounded-2xl overflow-hidden bg-slate-900 relative">
                {activeVideo ? (
                  <iframe 
                    src={activeVideo.url} 
                    title={activeVideo.title}
                    className="w-full h-full border-0"
                    allowFullScreen
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white text-xs">
                    Select a video to play
                  </div>
                )}
              </div>

              <div className="pt-4 text-white space-y-1">
                <span className="text-[10px] text-[#60A5FA] font-bold uppercase tracking-wider">Now Playing</span>
                <h4 className="font-serif-title text-base font-bold">{activeVideo?.title}</h4>
              </div>
            </div>

            {/* Right: Videos List Structure */}
            <div className="lg:w-1/3 p-6 bg-[#FAF8F5] overflow-y-auto max-h-[90vh] flex flex-col justify-between">
              <div className="space-y-4">
                <div>
                  <span className="text-[10px] font-bold text-[#16325B] uppercase tracking-widest block">Curriculum</span>
                  <h3 className="font-serif-title text-lg font-bold text-[#0B2540]">{selectedCourse.title}</h3>
                </div>

                {/* Video Lessons List */}
                <div className="space-y-2 pt-2">
                  {selectedCourse.videos.map((vid) => {
                    const userHasPurchased = isPurchased(selectedCourse.id);
                    const isUnlocked = vid.isFree || userHasPurchased;

                    return (
                      <div
                        key={vid.id}
                        onClick={() => {
                          if (isUnlocked) {
                            setActiveVideo(vid);
                          } else {
                            handlePurchaseClick(selectedCourse);
                          }
                        }}
                        className={`p-3.5 rounded-2xl border transition cursor-pointer flex items-center justify-between gap-3 ${
                          activeVideo?.id === vid.id 
                            ? 'bg-white border-[#16325B] shadow-sm ring-1 ring-[#16325B]/20' 
                            : 'bg-white/60 border-gray-200/80 hover:bg-white'
                        }`}
                      >
                        <div className="space-y-0.5">
                          <p className={`text-xs font-bold ${isUnlocked ? 'text-[#0B2540]' : 'text-gray-400'}`}>
                            {vid.title}
                          </p>
                          <span className="text-[10px] text-gray-400">{vid.duration}</span>
                        </div>

                        {/* Lock / Unlock Icon */}
                        <div>
                          {isUnlocked ? (
                            <span className="text-[#16325B] bg-[#EEF4FA] p-1.5 rounded-full block">
                              <Play className="w-3.5 h-3.5 fill-current" />
                            </span>
                          ) : (
                            <span className="text-gray-400 bg-gray-100 p-1.5 rounded-full block">
                              <Lock className="w-3.5 h-3.5" />
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Purchase Trigger inside Modal if Locked */}
              {!isPurchased(selectedCourse.id) && (
                <div className="pt-6 border-t border-gray-200 mt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 font-semibold">Course Access</span>
                    <span className="text-lg font-extrabold text-[#0B2540]">₹{selectedCourse.price}</span>
                  </div>

                  <button
                    onClick={() => handlePurchaseClick(selectedCourse)}
                    disabled={processingPayment}
                    className="w-full bg-[#16325B] hover:bg-[#0F213D] text-white font-bold py-3 rounded-2xl text-xs transition shadow-md disabled:opacity-50"
                  >
                    {processingPayment ? 'Unlocking Course...' : `Purchase & Unlock All (${selectedCourse.videos.length}) Videos`}
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>
      )}

    </div>
  );
}